import { Bbox } from 'ais-facade-api/api';

export enum PaletteEnum {
  Temperature = 'temperature',
  Waves = 'waves',
  Pressure = 'pressure'
}

export const TEMPERATURE_PALETTE = [
  [98, 50, 170, 255], // rgb(98, 50, 170);
  [0, 0, 110, 255], // rgb(0, 0, 110);
  [40, 104, 203, 255], // rgb(40, 104, 203);
  [40, 164, 195, 255], // rgb(40, 164, 195);
  [23, 138, 75, 255], // rgb(23, 138, 75);
  [109, 217, 141, 255], // rgb(109, 217, 141);
  [233, 239, 163, 255], // rgb(233, 239, 163);
  [240, 236, 24, 255], // rgb(240, 236, 24);
  [211, 83, 0, 255], // rgb(211, 83, 0);
  [190, 0, 0, 255], // rgb(190, 0, 0);
];

const WAVES_PALETTE = [
  [242, 249, 33, 255], // rgb(242, 249, 33);
  [243, 240, 39, 255], // rgb(243, 240, 39);
  [246, 232, 38, 255], // rgb(246, 232, 38);
  [248, 223, 37, 255], // rgb(248, 223, 37);
  [251, 215, 36, 255], // rgb(251, 215, 36);
  [252, 206, 37, 255], //rgb(252, 206, 37);
  [253, 198, 39, 255], //rgb(253, 198, 39);
  [254, 190, 42, 255], // rgb(254, 190, 42);
  [254, 183, 45, 255], // rgb(254, 183, 45);
  [253, 175, 49, 255], // rgb(253, 175, 49);
  [252, 168, 53, 255], // rgb(252, 168, 53);
  [251, 161, 57, 255], // rgb(251, 161, 57);
  [249, 154, 62, 255], // rgb(249, 154, 62);
  [247, 147, 66, 255], // rgb(247, 147, 66);
  [245, 140, 70, 255], // rgb(245, 140, 70);
  [243, 133, 75, 255], // rgb(243, 133, 75);
  [240, 127, 79, 255], // rgb(240, 127, 79);
  [237, 121, 83, 255], // rgb(237, 121, 83);
  [233, 114, 87, 255], // rgb(233, 114, 87);
  [230, 108, 92, 255], // rgb(230, 108, 92);
  [226, 102, 96, 255], // rgb(226, 102, 96);
  [222, 97, 100, 255], // rgb(222, 97, 100);
  [218, 91, 105, 255], // rgb(218, 91, 105);
  [214, 85, 109, 255], // rgb(214, 85, 109);
  [210, 79, 113, 255], // rgb(210, 79, 113);
  [205, 74, 118, 255], // rgb(205, 74, 118);
  [201, 68, 122, 255], // rgb(201, 68, 122);
  [196, 62, 127, 255], // rgb(196, 62, 127);
  [191, 57, 132, 255], // rgb(191, 57, 132);
  [186, 51, 136, 255], // rgb(186, 51, 136);
  [180, 46, 141, 255], // rgb(180, 46, 141);
  [174, 40, 146, 255], // rgb(174, 40, 146);
  [168, 34, 150, 255], // rgb(168, 34, 150);
  [162, 29, 154, 255], // rgb(162, 29, 154);
  [156, 23, 158, 255], // rgb(156, 23, 158);
  [149, 17, 161, 255], // rgb(149, 17, 161);
  [142, 12, 164, 255], // rgb(142, 12, 164);
  [135, 7, 166, 255], // rgb(135, 7, 166);
  [128, 4, 168, 255], // rgb(128, 4, 168);
  [120, 1, 168, 255], // rgb(120, 1, 168);
  [113, 0, 168, 255], // rgb(113, 0, 168);
  [105, 0, 168, 255], // rgb(105, 0, 168);
  [97, 0, 167, 255], // rgb(97, 0, 167);
  [89, 1, 165, 255], // rgb(89, 1, 165);
  [81, 2, 163, 255], // rgb(81, 2, 163);
  [73, 3, 160, 255], // rgb(73, 3, 160);
  [65, 4, 157, 255], // rgb(65, 4, 157);
  [56, 4, 154, 255], // rgb(56, 4, 154);
  [47, 5, 150, 255], // rgb(47, 5, 150);
  [38, 5, 145, 255], // rgb(38, 5, 145);
  [27, 6, 141, 255], // rgb(27, 6, 141);
  [13, 8, 135, 255], // rgb(13, 8, 135);
  [0, 0, 0, 0], // transparent
];

export const toEPSG3857 = (bbox: Bbox) => {
  const R = 6378137; // радиус Земли в метрах;

  const latToRad = (lat: number) => lat * (Math.PI / 180);
  const lonToRad = (lon: number) => lon * (Math.PI / 180);
  const lonToX = (lon: number) => R * lonToRad(lon);
  const latToY = (lat: number) => {
    return R * Math.log(Math.tan(Math.PI / 4 + latToRad(lat) / 2));
  };

  const first = {
    latitude: latToY(bbox.first.latitude),
    longitude: lonToX(bbox.first.longitude),
  };

  const second = {
    latitude: latToY(bbox.second.latitude),
    longitude: lonToX(bbox.second.longitude),
  };

  return {
    first,
    second,
    z: bbox.z, // масштаб остается неизменным
  };
};

export const normalizeWeatherData = (data: Uint8ClampedArray) => {
  const intensityValues = [];
  for (let i = 0; i < data.length; i += 4) {
    const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
    intensityValues.push(avg);
  }

  // Нормализация в диапазон [0,1]
  const arrayMinMax = (arr: number[]) =>
    arr.reduce(
      ([min, max], val) => [Math.min(min, val), Math.max(max, val)],
      [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY],
    );

  const [min, max] = arrayMinMax(intensityValues);
  const normalized = intensityValues.map((value) => (value - min) / (max - min));
  return normalized;
};

export const getColor = (palette: string, value: number) => {
  const usedPalette =  WAVES_PALETTE;

  // 2 соседние точки в палитре
  const index = Math.floor(value * (usedPalette.length - 1));
  const nextIndex = Math.min(index + 1, usedPalette.length - 1);
  const start_color = usedPalette[index];
  const end_color = usedPalette[nextIndex];

  const ratio = value * (palette.length - 1) - index;

  // Интерполируем цвет
  const interpolatedColor =
    palette === 'waves' ? start_color : start_color.map((c, i) => Math.round(c + (end_color[i] - c) * ratio));

  return interpolatedColor;
};

export const createColoredMap = async (viewState: Record<string, number>, normalizedData: number[], palette: PaletteEnum) => {
  const coloredCanvas = document.createElement('canvas');
  const coloredCtx = coloredCanvas.getContext('2d');

  coloredCanvas.width = viewState.width;
  coloredCanvas.height = viewState.height;

  if (coloredCtx) {
    const coloredData = coloredCtx.createImageData(coloredCanvas.width, coloredCanvas.height);

    for (let i = 0; i < normalizedData.length; i++) {
      const [r, g, b, a] = getColor(palette, normalizedData[i]);
      coloredData.data[i * 4] = r;
      coloredData.data[i * 4 + 1] = g;
      coloredData.data[i * 4 + 2] = b;
      coloredData.data[i * 4 + 3] = a;
    }

    coloredCtx.putImageData(coloredData, 0, 0);

    return new Promise((resolve) => {
      coloredCanvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    });
  }

  return new Promise((resolve) => {
    resolve(undefined);
  });
};
